import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../lib/api';

/**
 * WhatsAppFAB - a floating "Chat on WhatsApp" button anchored bottom-right.
 *
 * - Reads whatsapp number + business name from /api/business-settings/public
 * - Hidden on admin dashboard routes (/dharmikdashboard/*)
 * - Hidden if no whatsapp number is configured
 * - Opens wa.me deep-link in a new tab with a pre-filled message
 */

// Simple in-memory cache so we don't refetch on every route change
let _cache = null;
let _pending = null;

async function fetchSettings() {
    if (_cache) return _cache;
    if (_pending) return _pending;
    _pending = api.get('/business-settings/public')
        .then(r => { _cache = r.data || {}; return _cache; })
        .catch(() => ({}))
        .finally(() => { _pending = null; });
    return _pending;
}

function normalizeNumber(raw) {
    if (!raw) return '';
    // Strip everything except digits & leading +
    let s = String(raw).trim().replace(/[^\d+]/g, '');
    if (s.startsWith('+')) s = s.slice(1);
    // If it looks like a plain 10-digit Indian mobile, prepend country code 91
    if (/^\d{10}$/.test(s)) s = '91' + s;
    return s;
}

export function WhatsAppFAB() {
    const location = useLocation();
    const [num, setNum] = useState('');
    const [bizName, setBizName] = useState('');
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let mounted = true;
        fetchSettings().then(s => {
            if (!mounted) return;
            setNum(normalizeNumber(s?.whatsapp || s?.phone));
            setBizName(s?.name || 'Dharmik Gardens');
        });
        return () => { mounted = false; };
    }, []);

    // Hide on admin dashboard, login, checkout success (typography-first pages)
    const path = location.pathname;
    const hidden = path.startsWith('/dharmikdashboard') || path.startsWith('/login');
    if (hidden || !num) return null;

    const msg = encodeURIComponent(`Hi ${bizName}, I would like to know more about your products.`);
    const url = `https://wa.me/${num}?text=${msg}`;

    return (
        <div
            className="fixed z-[9999] bottom-5 right-5 sm:bottom-6 sm:right-6 flex items-end gap-2"
            data-testid="whatsapp-fab-root"
        >
            {/* Tooltip / label bubble */}
            <div
                className={`hidden sm:flex items-center gap-2 rounded-full bg-white shadow-lg border pr-4 pl-3 py-2 transition-all duration-200 ${expanded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-2 pointer-events-none'}`}
                aria-hidden={!expanded}
            >
                <div className="text-xs font-semibold text-neutral-800">Chat on WhatsApp</div>
                <div className="text-[10px] text-neutral-500">·  Reply in minutes</div>
            </div>

            <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                onMouseEnter={() => setExpanded(true)}
                onMouseLeave={() => setExpanded(false)}
                onFocus={() => setExpanded(true)}
                onBlur={() => setExpanded(false)}
                aria-label={`Chat on WhatsApp with ${bizName}`}
                title={`Chat on WhatsApp with ${bizName}`}
                data-testid="whatsapp-fab"
                className="group relative flex items-center justify-center h-14 w-14 rounded-full shadow-xl transition-transform duration-200 hover:scale-105 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-green-300"
                style={{ background: '#25D366' }}
            >
                {/* Pulse ring */}
                <span className="absolute inset-0 rounded-full animate-ping opacity-30" style={{ background: '#25D366' }} />
                {/* WhatsApp glyph */}
                <svg viewBox="0 0 32 32" width="30" height="30" aria-hidden="true">
                    <path
                        fill="#ffffff"
                        d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.788 0 2.192-.42 2.514-1.05.19-.372.36-.988.36-1.446 0-.128 0-.257-.043-.386-.147-.294-2.13-1.13-2.485-1.13zm-2.98 6.657h-.017c-1.548 0-3.067-.42-4.39-1.207l-.316-.187-3.263.854.874-3.178-.216-.33a8.62 8.62 0 0 1-1.318-4.594c0-4.795 3.896-8.69 8.69-8.69 2.323 0 4.502.907 6.138 2.547a8.635 8.635 0 0 1 2.55 6.144c0 4.795-3.898 8.65-8.734 8.65zm7.396-16.045a10.421 10.421 0 0 0-7.412-3.07c-5.79 0-10.499 4.71-10.5 10.5 0 1.85.482 3.66 1.4 5.253l-1.487 5.433 5.559-1.457a10.505 10.505 0 0 0 5.019 1.28h.005c5.79 0 10.501-4.71 10.501-10.499a10.435 10.435 0 0 0-3.084-7.44z"
                    />
                </svg>
            </a>
        </div>
    );
}

// Allow admin BusinessSettings save handler to invalidate cache
export function invalidateWhatsAppCache() { _cache = null; }

export default WhatsAppFAB;
